/**
 * Global Functionality
 * The logic in this is intended to ONLY contain any functionality
 * that is required on every page load.
 *
 * NOTE: Please do not include any page-specific logic here.
 **/

import Logger from "../lib/logger";
import ThemeFloatingAvatar from './ThemeFloatingAvatar';
import ThemeHeader from './ThemeHeader';
import confetti from 'canvas-confetti';
import { gsap } from "gsap";
import { Observer } from 'gsap/Observer';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(Observer, ScrollSmoother, ScrollTrigger, SplitText);

export default function () {
	const NAME = 'Global';
	Logger.info(`${NAME} > loading`);

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	const breakpoints = {
		tablet_min: 768,
		laptop_min: 1024,
		desktop_min: 1440,
		desktop_max: 1600,
		large_desktop_min: 1441,
	};

	let currentBreakpoint = null;
	let previousBreakpoint = null;

	let windowHeight = window.innerHeight;
	let windowWidth = window.innerWidth;

	const confettiDoms = document.querySelectorAll(`[data-confetti]`);

	const scrollContentDom = document.querySelector(`[data-scroll-content]`);
	const scrollWrapperDom = document.querySelector(`[data-scroll-wrapper]`);

	const themeFooterContactContentDom = document.querySelector(`[data-footer-contact-content]`);
	const themeFooterContactHeadlineDom = document.querySelector(`[data-footer-contact-headline]`);
	let themeFooterContactImageAnimation;
	const themeFooterContactImageDom = document.querySelector(`[data-footer-contact-image]`);
	const themeFooterContactParagraphDom = document.querySelector(`[data-footer-contact-paragraph]`);

	let themeFloatingAvatar;
	const themeFloatingAvatarDom = document.querySelector(`[data-theme-floating-avatar]`);

	let themeHeader;
	const themeHeaderDom = document.querySelector(`[data-theme-header]`);

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * init --- Initialization block
	 */
	function init() {
		Logger.info(`${NAME} > init`);

		initRecordWindowSize();
		// initScrollEffects();

		initCheatCodes();
		// initConfetti();
		initNoScroll();
		initThemeFloatingAvatar();
		initThemeFooterContact();
		initThemeHeader();
	}

	/**
	 * getCurrentBreakpoint - Get the current breakpoint
	 */
	const getCurrentBreakpoint = () => {
		Logger.info(`${NAME} > getCurrentBreakpoint`);

		// Record the previous breakpoint
		previousBreakpoint = currentBreakpoint;

		// Check window width and return the current breakpoint
		let breakpoint = ``;
		if (windowWidth < 720) {
			breakpoint = `mobile`;
		} else if (windowWidth < 1024) {
			breakpoint = `tablet`;
		} else if (windowWidth < 1024) {
			breakpoint = `laptop`;
		} else {
			breakpoint = `desktop`;
		}

		// Record the current breakpoint
		currentBreakpoint = breakpoint;

		return breakpoint;

	}

	/**
	 * initCheatCodes - Initialize cheat codes
	 */
	const initCheatCodes = () => {
		Logger.info(`${NAME} > initCheatCodes`);

		let bufferArray = [];
		let lastKeystrokeTime = Date.now();

		//Our cheat code
		const cheatcode = "hair";

		window.addEventListener("keyup", e => {
			const key = e.key.toLowerCase();
			const latestKeystrokeTime = Date.now();

			if (latestKeystrokeTime - lastKeystrokeTime > 1500) {
				bufferArray = [];
			}

			bufferArray.push(key);

			const word = bufferArray.join("");
			if (word === cheatcode) {
				// Dynamically a full screen overlay with the video: /wp-content/themes/wonderpress/static/dist/video/drstrange.mp4
				const videoOverlay = document.createElement("div");
				videoOverlay.classList.add("theme-dr-strange-video");
				videoOverlay.innerHTML = `
						<video autoplay playsinline>
							<source src="/wp-content/themes/wonderpress/static/dist/video/drstrange.mp4" type="video/mp4">
						</video>
				`;
				document.body.appendChild(videoOverlay);

				// Remove the video when clicked
				videoOverlay.addEventListener("click", () => {
					videoOverlay.remove();
				});

				// Remove the video when it ends
				videoOverlay.querySelector("video").addEventListener("ended", () => {
					videoOverlay.remove();
				});
			}

			lastKeystrokeTime = latestKeystrokeTime;
		});
	}

	/**
	 * initConfetti - Initialize the confetti
	 */
	const initConfetti = () => {
		Logger.info(`${NAME} > initConfetti`);

		confettiDoms.forEach((confettiDom) => {

			confettiDom.addEventListener(`mouseenter`, () => {
				const rect = confettiDom.getBoundingClientRect();
				const xOrigin = (rect.left + (rect.width / 2)) / windowWidth;
				const yOrigin = (rect.top + (rect.height / 2)) / windowHeight;

				confetti({
					particleCount: 100,
					spread: 70,
					origin: { x: xOrigin, y: yOrigin },
					colors: ["#FFFFFF"],
				});
			});
		});
	}

	/**
	 * initNoScroll - Initialize the no scroll
	 */
	const initNoScroll = () => {
		Logger.info(`${NAME} > initNoScroll`);

		// Listen for event to trigger no scroll
		document.addEventListener(`make-no-scroll`, (e) => {
			// Prevent scrolling
			document.body.style.overflow = `hidden`;

			// Add a class to the body
			document.body.classList.add(`no-scroll`);

			// Listen for scroll events
			window.addEventListener(`scroll`, (e) => {
				e.preventDefault();
				e.stopPropagation();
				return false;
			}, { passive: false });
		});

		// Listen for event to remove no scroll
		document.addEventListener(`remove-no-scroll`, (e) => {
			// Allow scrolling
			document.body.style.overflow = ``;

			// Remove the class from the body
			document.body.classList.remove(`no-scroll`);

			// Remove the scroll event listener
			window.removeEventListener(`scroll`, (e) => {
				e.preventDefault();
				e.stopPropagation();
				return false;
			}, { passive: false });
		});
	}

	/**
	 * initRecordWindowSize - Record the window size
	 */
	const initRecordWindowSize = () => {
		Logger.info(`${NAME} > initRecordWindowSize`);

		// Listen for window resize events
		window.addEventListener(`resize`, () => {
			windowHeight = window.innerHeight;
			windowWidth = window.innerWidth;
			Logger.info(`${NAME} > initRecordWindowSize > windowHeight: ${windowHeight}, windowWidth: ${windowWidth}`);
		});

		// Trigger a resize event to record the initial window size
		setTimeout(() => {
			window.dispatchEvent(new Event(`resize`));
		}, 100);
	}

	/**
	 * initScrollEffects - Initialize scroll effects
	 */
	const initScrollEffects = () => {
		Logger.info(`${NAME} > initScrollEffects`);

		let mm = gsap.matchMedia();

		// add a media query. When it matches, the associated function will run
		mm.add(`(min-width: ${breakpoints.laptop_min}px)`, () => {

			const smoother = ScrollSmoother.create({
				content: scrollContentDom, // the element that has the scrollbar
				wrapper: scrollWrapperDom, // the element that contains the content
				smooth: 1, // how long (in seconds) it takes to "catch up" to the native scroll position
				effects: true, // looks for data-speed and data-lag attributes on elements
				// effectsPrefix: "scroll", // the prefix for data attributes that trigger effects
				smoothTouch: 0.1, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
			});

			// optional
			// custom cleanup code here (runs when it STOPS matching)
			return () => {
				smoother.kill();
			};
		});
	}

	/**
	 * initThemeFloatingAvatar --- Initialize the ThemeFloatingAvatar
	 */
	const initThemeFloatingAvatar = () => {
		Logger.info(`${NAME} > initThemeFloatingAvatar`);

		if (themeFloatingAvatarDom) {
			themeFloatingAvatar = new ThemeFloatingAvatar(themeFloatingAvatarDom);
			themeFloatingAvatar.init();
		}
	}

	/**
	 * initThemeFooterContact - Initialize the global contact section
	 */
	const initThemeFooterContact = () => {
		Logger.info(`${NAME} > initThemeFooterContact`);

		if (themeFloatingAvatarDom) {
			gsap.set(themeFloatingAvatarDom, { opacity: 1 });
			gsap.to(themeFloatingAvatarDom, {
				translateX: 180,
				rotate: 70,
				duration: 0.6,
				ease: "power1.inOut",
				scrollTrigger: {
					trigger: `[data-footer-contact]`,
					start: "top bottom+=500px",
					end: "top bottom",
					scrub: true,

					onEnter: () => {
						themeFloatingAvatar.stop();
					}
				}
			});
		}

		// Pin the contact content section
		// if (themeFooterContactHeadlineDom) {

		// 	// Wait a bit before animating the contact content
		// 	setTimeout(() => {

		// 		// Create a SplitText for the contact headline
		// 		var contactHeadlineSplit = new SplitText(themeFooterContactHeadlineDom, {
		// 			type: "chars,words,lines",
		// 			linesClass: "split-line",
		// 		});
		// 		const contactHeadlineSplitAnim = gsap.from(contactHeadlineSplit.chars, {
		// 			duration: 0.6,
		// 			ease: "power1.inOut",
		// 			y: 40,
		// 			opacity: 0,
		// 			stagger: 0.02,
		// 		});

		// 		// Create a SplitText for the contact headline
		// 		var contactParagraphSplit = new SplitText(themeFooterContactParagraphDom, {
		// 			type: "chars,words,lines",
		// 			linesClass: "split-line",
		// 		});
		// 		const contactParagraphSplitAnim = gsap.from(contactParagraphSplit.chars, {
		// 			duration: 0.6,
		// 			ease: "power1.inOut",
		// 			y: 20,
		// 			opacity: 0,
		// 		});

		// 		// Show the contact content
		// 		gsap.set(themeFooterContactContentDom, { opacity: 1 });
		// 		contactHeadlineSplitAnim.progress(0).pause();
		// 		contactParagraphSplitAnim.progress(0).pause();

		// 		ScrollTrigger.create({
		// 			trigger: themeFooterContactContentDom,
		// 			start: "top 60%",
		// 			onEnter: () => {
		// 				contactHeadlineSplitAnim.restart();
		// 				contactParagraphSplitAnim.restart();
		// 			},
		// 			onLeaveBack: () => {
		// 				contactHeadlineSplitAnim.reverse();
		// 				contactParagraphSplitAnim.reverse();
		// 			}
		// 		});

		// 	}, 300);
		// }

		// Make the contact image peek out
		if (themeFooterContactImageDom) {

			// Listen for resize events
			window.addEventListener(`resize`, () => {
				setThemeFooterContactImageScrollTrigger();
			});

			setThemeFooterContactImageScrollTrigger();
		}
	}

	/**
	 * initThemeHeader --- Initialize the ThemeHeader
	 */
	const initThemeHeader = () => {
		Logger.info(`${NAME} > initThemeHeader`);

		if (themeHeaderDom) {
			themeHeader = new ThemeHeader(themeHeaderDom);
			themeHeader.init();
		}
	}

	/**
	 * setThemeFooterContactImageScrollTrigger - Set the contact image scroll trigger
	 */
	const setThemeFooterContactImageScrollTrigger = () => {
		Logger.info(`${NAME} > setThemeFooterContactImageScrollTrigger`);

		const currentBreakpoint = getCurrentBreakpoint();

		// If the breakpoint changed, reset the contact image
		if (previousBreakpoint !== currentBreakpoint) {
			if (themeFooterContactImageAnimation) {
				themeFooterContactImageAnimation.scrollTrigger?.kill();
			}
		}

		if (currentBreakpoint === `mobile`) {
			// Do nothing
		} else {
			if (themeFooterContactImageDom) {
				gsap.set(themeFooterContactImageDom, {
					translateX: `200%`,
					rotate: 100,
				});
				themeFooterContactImageAnimation = gsap.to(themeFooterContactImageDom, {
					translateX: 0,
					rotate: 0,
					ease: `power1.inOut`,
					scrollTrigger: {
						trigger: themeFooterContactImageDom,
						start: "top 80%",
						end: "top 30%",
						scrub: true,
					}
				});
			}
		}
	}

	// Do not edit below this line...
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	Logger.info(`${NAME} > loaded`);

	init();

	return {
		breakpoints: breakpoints,
		currentBreakpoint: currentBreakpoint,
		getCurrentBreakpoint: getCurrentBreakpoint,
		initScrollEffects: initScrollEffects,
		previousBreakpoint: previousBreakpoint,
		themeHeader: themeHeader,
		themeFloatingAvatar: themeFloatingAvatar,
		windowHeight: windowHeight,
		windowWidth: windowWidth,
	}
}
